import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Textarea } from '../components/Textarea';
import { Select } from '../components/Select';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

interface FormErrors {
  title?: string;
  primaryGenre?: string;
  wordCount?: string;
  synopsis?: string;
  firstPages?: string;
  general?: string;
  [key: string]: string | undefined;
}

export const CreateManuscriptPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    primaryGenre: '',
    subGenre: '',
    wordCount: '',
    synopsis: '',
    firstPages: '',
    compTitle1: '',
    compTitle2: '',
    compTitle3: '',
    contentWarnings: '',
    saveAsDraft: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  const genres = [
    { value: 'FANTASY', label: 'Fantasy' },
    { value: 'SCIENCE_FICTION', label: 'Science Fiction' },
    { value: 'MYSTERY', label: 'Mystery' },
    { value: 'THRILLER', label: 'Thriller' },
    { value: 'ROMANCE', label: 'Romance' },
    { value: 'LITERARY_FICTION', label: 'Literary Fiction' },
    { value: 'HISTORICAL_FICTION', label: 'Historical Fiction' },
    { value: 'HORROR', label: 'Horror' },
    { value: 'YOUNG_ADULT', label: 'Young Adult' },
    { value: 'MIDDLE_GRADE', label: 'Middle Grade' },
    { value: 'CONTEMPORARY', label: 'Contemporary' },
    { value: 'PARANORMAL', label: 'Paranormal' },
    { value: 'DYSTOPIAN', label: 'Dystopian' },
    { value: 'OTHER', label: 'Other' },
  ];

  const subGenres: Record<string, Array<{ value: string; label: string }>> = {
    'literary-fiction': [
      { value: 'contemporary', label: 'Contemporary' },
      { value: 'experimental', label: 'Experimental' },
      { value: 'southern-gothic', label: 'Southern Gothic' },
    ],
    'mystery': [
      { value: 'cozy', label: 'Cozy Mystery' },
      { value: 'hardboiled', label: 'Hardboiled' },
      { value: 'procedural', label: 'Police Procedural' },
    ],
    'fantasy': [
      { value: 'epic', label: 'Epic Fantasy' },
      { value: 'urban', label: 'Urban Fantasy' },
      { value: 'dark', label: 'Dark Fantasy' },
    ],
    // Add more as needed
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};

    if (!formData.title) {
      newErrors.title = 'Title is required';
    }

    if (!formData.primaryGenre) {
      newErrors.primaryGenre = 'Primary genre is required';
    }

    if (!formData.wordCount) {
      newErrors.wordCount = 'Word count is required';
    } else if (isNaN(Number(formData.wordCount)) || Number(formData.wordCount) < 1) {
      newErrors.wordCount = 'Please enter a valid word count';
    }

    if (!formData.synopsis) {
      newErrors.synopsis = 'Synopsis is required';
    } else if (formData.synopsis.split(' ').length > 500) {
      newErrors.synopsis = 'Synopsis must be 500 words or less';
    }

    if (!formData.firstPages) {
      newErrors.firstPages = 'First pages are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (isDraft = false) => {
    if (!isDraft && !validateForm()) {
      return;
    }

    setIsSaving(true);
    setErrors({});

    try {
      // Prepare comp titles array
      const compTitles = [
        formData.compTitle1,
        formData.compTitle2,
        formData.compTitle3,
      ].filter(title => title.trim() !== '');

      // Prepare content warnings array
      const contentWarnings = formData.contentWarnings
        ? formData.contentWarnings.split(',').map(w => w.trim()).filter(w => w !== '')
        : [];

      // Prepare data for API
      const manuscriptData = {
        title: formData.title,
        primaryGenre: formData.primaryGenre,
        subGenre: formData.subGenre || undefined,
        wordCount: parseInt(formData.wordCount),
        synopsis: formData.synopsis,
        firstPages: formData.firstPages || undefined,
        compTitles: compTitles.length > 0 ? compTitles : undefined,
        contentWarnings: contentWarnings.length > 0 ? contentWarnings : undefined,
      };

      const response = await fetch(API_ENDPOINTS.manuscripts.create, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(manuscriptData),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.details) {
          const newErrors: FormErrors = {};
          data.details.forEach((detail: any) => {
            const field = detail.path?.[0] || 'general';
            newErrors[field] = detail.message;
          });
          setErrors(newErrors);
        } else {
          setErrors({ general: data.error || 'Failed to create manuscript' });
        }
        setIsSaving(false);
        return;
      }

      console.log('Manuscript created successfully:', data);
      window.location.href = '/writer/manuscripts';
    } catch (error) {
      console.error('Create manuscript error:', error);
      setErrors({ general: 'Network error. Please check your connection and try again.' });
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to discard this manuscript?')) {
      window.location.href = '/writer/manuscripts';
    }
  };

  const currentSubGenres = formData.primaryGenre && subGenres[formData.primaryGenre]
    ? subGenres[formData.primaryGenre]
    : [];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Page Header */}
        <div style={{
          marginBottom: '40px',
        }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '12px',
            color: theme.colors.textPrimary,
          }}>
            Add New Manuscript
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Share your work with beta readers
          </p>
        </div>

        {/* Form */}
        <div style={{
          backgroundColor: theme.colors.white,
          borderRadius: theme.borderRadius.xlarge,
          padding: '40px',
          border: `1px solid ${theme.colors.border}`,
          marginBottom: '24px',
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
          }}>
            {/* Title */}
            <Input
              label="Title"
              type="text"
              placeholder="Enter your manuscript title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              error={errors.title}
              required
            />

            {/* Genre Selection */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
            }}>
              <Select
                label="Primary Genre"
                value={formData.primaryGenre}
                onChange={(e) => setFormData({ ...formData, primaryGenre: e.target.value, subGenre: '' })}
                options={genres}
                placeholder="Select primary genre"
                error={errors.primaryGenre}
                required
              />

              <Select
                label="Sub-Genre"
                value={formData.subGenre}
                onChange={(e) => setFormData({ ...formData, subGenre: e.target.value })}
                options={currentSubGenres}
                placeholder={currentSubGenres.length > 0 ? 'Select sub-genre' : 'Select primary genre first'}
                helperText="Optional"
              />
            </div>

            {/* Word Count */}
            <Input
              label="Word Count"
              type="number"
              placeholder="e.g., 85000"
              value={formData.wordCount}
              onChange={(e) => setFormData({ ...formData, wordCount: e.target.value })}
              error={errors.wordCount}
              helperText="Approximate word count of your complete manuscript"
              required
            />

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>

            {/* Synopsis */}
            <Textarea
              label="Synopsis"
              placeholder="Provide a compelling synopsis of your manuscript..."
              value={formData.synopsis}
              onChange={(e) => setFormData({ ...formData, synopsis: e.target.value })}
              maxLength={5000}
              rows={8}
              error={errors.synopsis}
              helperText="Maximum 500 words - This will be visible to beta readers"
              required
            />

            {/* First Pages */}
            <Textarea
              label="First Pages"
              placeholder="Paste the first 10-15 pages or first chapter..."
              value={formData.firstPages}
              onChange={(e) => setFormData({ ...formData, firstPages: e.target.value })}
              rows={12}
              error={errors.firstPages}
              helperText="This sample helps beta readers decide if they want to read your manuscript"
              required
            />

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: theme.colors.border,
            }}></div>

            {/* Comp Titles */}
            <div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Comparative Titles
              </h3>
              <p style={{
                fontSize: '15px',
                color: theme.colors.textSecondary,
                marginBottom: '20px',
                lineHeight: '1.6',
              }}>
                List 2-3 published books that are similar to yours in style, tone, or content
              </p>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}>
                <Input
                  placeholder="e.g., Title by Author"
                  value={formData.compTitle1}
                  onChange={(e) => setFormData({ ...formData, compTitle1: e.target.value })}
                />
                <Input
                  placeholder="e.g., Title by Author"
                  value={formData.compTitle2}
                  onChange={(e) => setFormData({ ...formData, compTitle2: e.target.value })}
                />
                <Input
                  placeholder="e.g., Title by Author (Optional)"
                  value={formData.compTitle3}
                  onChange={(e) => setFormData({ ...formData, compTitle3: e.target.value })}
                />
              </div>
            </div>

            {/* Content Warnings */}
            <Textarea
              label="Content Warnings"
              placeholder="List any content warnings (violence, explicit content, sensitive topics, etc.)"
              value={formData.contentWarnings}
              onChange={(e) => setFormData({ ...formData, contentWarnings: e.target.value })}
              maxLength={500}
              rows={3}
              helperText="Optional but recommended - Helps beta readers make informed decisions"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <div style={{
            display: 'flex',
            gap: '12px',
          }}>
            <Button variant="secondary" onClick={() => handleSave(true)} disabled={isSaving}>
              Save as Draft
            </Button>
            <Button onClick={() => handleSave(false)} disabled={isSaving}>
              {isSaving ? 'Publishing...' : 'Publish Manuscript'}
            </Button>
          </div>
        </div>

        {/* Help Text */}
        <div style={{
          marginTop: '32px',
          padding: '20px',
          backgroundColor: theme.colors.backgroundAlt,
          borderRadius: theme.borderRadius.large,
          fontSize: '15px',
          lineHeight: '1.6',
          color: theme.colors.textSecondary,
        }}>
          <strong style={{ color: theme.colors.textPrimary }}>Tips for success:</strong>
          <ul style={{ marginTop: '12px', paddingLeft: '20px' }}>
            <li>Make your synopsis engaging - it's how beta readers decide to apply</li>
            <li>Include comp titles that are recent (published within the last 3-5 years)</li>
            <li>Be upfront about content warnings to find the right readers</li>
            <li>You can save as draft and come back to edit before publishing</li>
          </ul>
        </div>
      </main>

      <Footer />
    </div>
  );
};
